import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { Eye, EyeOff } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

import logo from "../../assets/2 trend.webp";
import front from "../../assets/rightbanner.webp";
import useRegister from "../Services/Login/useRegister";

const Register = ({ isBookingFlow = false, onNext }) => {
  const [showPassword, setShowPassword] = useState(false);

  const { registerLoading, registerUserApi } = useRegister();

  const [registerData, setRegisterData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    usertype: "customer",
  });

  const navigate = useNavigate();

  const validateRegisterForm = () => {
    if (!registerData.firstName.trim()) {
      toast.error("Please enter first name");
      return false;
    }

    if (!registerData.lastName.trim()) {
      toast.error("Please enter last name");
      return false;
    }

    if (!registerData.email.trim()) {
      toast.error("Please enter email address");
      return false;
    }

    if (!registerData.phone.trim() || registerData.phone.length !== 10) {
      toast.error("Please enter valid 10 digit phone number");
      return false;
    }

    if (!registerData.password) {
      toast.error("Please enter password");
      return false;
    }

    if (!registerData.confirmPassword) {
      toast.error("Please enter confirm password");
      return false;
    }

    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Password and Confirm Password should be same");
      return false;
    }

    return true;
  };

  const handleRegisterSubmit = async () => {
    if (!validateRegisterForm()) return;

    const result = await registerUserApi(registerData);

    console.log("Register Result:", result);

    if (!result) return;

    if (result.success) {
      toast.success(result.message || "Registration successful");

      setRegisterData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        usertype: "customer",
      });

      setTimeout(() => {
        if (isBookingFlow && onNext) {
          onNext();
        } else {
          navigate("/gateway");
        }
      }, 1000);
    } else {
      toast.error(result.message || "Registration failed");
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div
        className={
          isBookingFlow
            ? "relative w-full overflow-hidden"
            : "relative min-h-screen w-full overflow-hidden bg-gradient-to-r from-[#973903] to-[#ec8107]"
        }
      >
        <div
          className={
            isBookingFlow
              ? "relative z-10 flex w-full items-center justify-center px-4"
              : "relative z-10 flex min-h-screen items-center justify-center px-4"
          }
        >
          <div
            className={
              isBookingFlow
                ? "w-full max-w-[990px] bg-white rounded-xl mt-[calc(24rem+72px)] overflow-hidden shadow-xl grid grid-cols-1 lg:grid-cols-[55%_45%]"
                : "w-full max-w-[990px] bg-white rounded-xl overflow-hidden shadow-xl grid grid-cols-1 lg:grid-cols-[55%_45%]"
            }
          >
            {/* LEFT FORM */}
            <div className="p-5 lg:px-10 py-5">
              <div className="flex h-full flex-col justify-center">
                <div className="mb-6 flex items-center gap-2">
                  <img src={logo} alt="logo" className="h-16" />
                </div>

                <div className="mb-6">
                  <h2 className="text-[24px] font-semibold text-gray-700">
                    Create Account
                  </h2>

                  <p className="mt-2 text-[14px] text-gray-400">
                    Register with your details to continue.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <input
                      type="text"
                      placeholder="First Name"
                      value={registerData.firstName}
                      onChange={(e) =>
                        setRegisterData({
                          ...registerData,
                          firstName: e.target.value,
                        })
                      }
                      className="w-full rounded-md border bg-white px-4 py-3 text-[14px] text-gray-600 outline-none focus:border-blue-400"
                    />

                    <input
                      type="text"
                      placeholder="Last Name"
                      value={registerData.lastName}
                      onChange={(e) =>
                        setRegisterData({
                          ...registerData,
                          lastName: e.target.value,
                        })
                      }
                      className="w-full rounded-md border bg-white px-4 py-3 text-[14px] text-gray-600 outline-none focus:border-blue-400"
                    />
                  </div>

                  <input
                    type="email"
                    placeholder="Email Address"
                    value={registerData.email}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        email: e.target.value,
                      })
                    }
                    className="w-full rounded-md border bg-white px-4 py-3 text-[14px] text-gray-600 outline-none focus:border-blue-400"
                  />

                  <input
                    type="text"
                    placeholder="Phone Number"
                    value={registerData.phone}
                    maxLength={10}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");

                      setRegisterData({
                        ...registerData,
                        phone: value,
                      });
                    }}
                    className="w-full rounded-md border bg-white px-4 py-3 text-[14px] text-gray-600 outline-none focus:border-blue-400"
                  />

                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      value={registerData.password}
                      onChange={(e) =>
                        setRegisterData({
                          ...registerData,
                          password: e.target.value,
                        })
                      }
                      className="w-full rounded-md border bg-white px-4 py-3 pr-16 text-[14px] text-gray-600 outline-none focus:border-blue-400"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-600"
                    >
                      {!showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>

                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Confirm Password"
                      value={registerData.confirmPassword}
                      onChange={(e) =>
                        setRegisterData({
                          ...registerData,
                          confirmPassword: e.target.value,
                        })
                      }
                      className="w-full rounded-md border bg-white px-4 py-3 pr-16 text-[14px] text-gray-600 outline-none focus:border-blue-400"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-600"
                    >
                      {!showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>

                  <button
                    onClick={handleRegisterSubmit}
                    disabled={registerLoading}
                    className="w-full rounded-lg bg-[#1f2d3f] py-3 font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {registerLoading ? "Submitting..." : "Submit"}
                  </button>

                  {!isBookingFlow && (
                    <button
                      className="w-full text-sm text-[#0c4d45]"
                      onClick={() => navigate("/gateway")}
                    >
                      Back to Login
                    </button>
                  )}

                  <div className="flex items-center justify-center gap-6 pt-5 text-[12px] text-[#444]">
                    <a href="/privacy" className="hover:underline">
                      Privacy
                    </a>

                    <a href="/terms&conditions" className="hover:underline">
                      Terms & Conditions
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="relative hidden h-full w-full items-center justify-center overflow-hidden bg-gradient-to-b from-[#eef8ff] to-[#5bb7f5] lg:flex">
              {/* BIG LETTER BACKGROUND */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[300px] font-black leading-none text-white opacity-20 xl:text-[400px]">
                  P
                </span>
              </div>

              {/* IMAGE */}
              <div className="relative z-10 flex h-full w-full items-center justify-center">
                <img
                  src={front}
                  alt="delivery"
                  className="max-h-[760px] w-full max-w-[520px] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;